<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { TIMEZONES, TIMEZONE_SET } from '@/constants/timezones'
import { timezoneOptionLabel } from '@/utils/localTime'

interface TimezoneOption {
  id: string
  label: string
}

interface TimezoneGroup {
  key: string
  label: string
  isOther: boolean
  options: TimezoneOption[]
}

const REGION_ORDER = [
  'Africa',
  'America',
  'Antarctica',
  'Arctic',
  'Asia',
  'Atlantic',
  'Australia',
  'Europe',
  'Indian',
  'Pacific',
] as const

const props = withDefaults(defineProps<{
  modelValue: string
  id: string
  invalid?: boolean
  disabled?: boolean
}>(), {
  invalid: false,
  disabled: false,
})

const emit = defineEmits<{
  'update:modelValue': [timezone: string]
}>()

const { t } = useI18n()

function buildOption(id: string): TimezoneOption {
  return { id, label: timezoneOptionLabel(id) }
}

// Labels are resolved once per mount, not per render.
const optionById = new Map<string, TimezoneOption>(TIMEZONES.map((id) => [id, buildOption(id)]))

function regionOf(id: string): string {
  const slash = id.indexOf('/')
  return slash === -1 ? '' : id.slice(0, slash)
}

function optionFor(id: string): TimezoneOption {
  const known = optionById.get(id)
  if (known) return known
  const created = buildOption(id)
  optionById.set(id, created)
  return created
}

const groups = computed<TimezoneGroup[]>(() => {
  const current = props.modelValue
  const byRegion = new Map<string, TimezoneOption[]>()
  const other: TimezoneOption[] = []

  const ids: string[] = [...TIMEZONES]
  // A stored value outside the list is preserved and shown under "Other".
  const currentIsUnknown = current !== '' && !TIMEZONE_SET.has(current)
  if (currentIsUnknown) ids.push(current)

  for (const id of ids) {
    const option = optionFor(id)
    const region = regionOf(id)
    const isRegional = (REGION_ORDER as ReadonlyArray<string>).includes(region) && !(currentIsUnknown && id === current)
    if (isRegional) {
      const list = byRegion.get(region) ?? []
      list.push(option)
      byRegion.set(region, list)
    } else {
      other.push(option)
    }
  }

  const result: TimezoneGroup[] = REGION_ORDER.filter((region) => byRegion.has(region)).map((region) => ({
    key: region,
    label: region,
    isOther: false,
    options: byRegion.get(region) ?? [],
  }))
  if (other.length > 0) {
    result.push({ key: '__other', label: t('timezones.group_other'), isOther: true, options: other })
  }
  return result
})

function handleChange(event: Event): void {
  emit('update:modelValue', (event.target as HTMLSelectElement).value)
}
</script>

<template>
  <select
    :id="id"
    :value="modelValue"
    :disabled="disabled"
    class="w-full rounded-md bg-slate-900 border px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 transition-colors"
    :class="invalid ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500'"
    @change="handleChange"
  >
    <option v-if="modelValue === ''" value="" disabled>
      {{ t('timezones.placeholder') }}
    </option>
    <optgroup v-for="group in groups" :key="group.key" :label="group.label" :data-testid="`timezone-group-${group.key}`">
      <option v-for="option in group.options" :key="option.id" :value="option.id">{{ option.label }}</option>
    </optgroup>
  </select>
</template>
